import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Loading } from '../ui';
import {
  coverUrl,
  ladeProduktMedien,
  PRODUKT_SLOT_LABEL,
  PRODUKT_VARIANTE_LABEL,
  type ProduktMedienAntwort,
  type ProduktSlotStand,
  type ProduktVariante,
} from './api';

/**
 * Auswahl „Aus Produkt-Medien …" im Seiten-Editor (Spec § 12): je Fach × Variante eine Kachel mit
 * Vorschau. Vorhandene Varianten sind waehlbar, fehlende ausgegraut, leere Faecher verweisen auf den
 * Reiter „Produkt-Medien". Die Auswahl liefert `fach:variante` + die aktuelle URL — die Bindung
 * setzt die Editor-Runtime im iframe (`data-lp-produktbild`).
 *
 * Gemountet per `open && …` (VisualEditor) → der Zustand lebt je Oeffnen.
 */

/** Varianten, die ein Fach ueberhaupt haben kann: `faecher` nur die Ausgabe. */
function variantenFuer(f: ProduktSlotStand): ProduktVariante[] {
  return f.slot === 'ausgabe' ? ['einzel', 'flach', 'faecher'] : ['einzel', 'flach'];
}

interface Props {
  webinarId: string;
  onClose: () => void;
  onWaehle: (bindung: string, url: string) => void;
}

export default function ProduktBildDialog({ webinarId, onClose, onWaehle }: Props) {
  const [antwort, setAntwort] = useState<ProduktMedienAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await ladeProduktMedien(webinarId);
      if (cancelled) return;
      if (r.success && r.data) setAntwort(r.data);
      else setFehler(r.error || 'Produkt-Medien konnten nicht geladen werden');
    })();
    return () => {
      cancelled = true;
    };
  }, [webinarId]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Bild aus Produkt-Medien
        {antwort?.plxProduct && (
          <Typography variant="caption" display="block" color="text.secondary">
            Produkt: {antwort.produktName || antwort.plxProduct} ({antwort.plxProduct}) — das Bild
            folgt künftig der aktuellen Fassung des Fachs (nach Prüfen und Publizieren)
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {fehler && <Alert severity="error">{fehler}</Alert>}
        {!antwort && !fehler && <Loading label="Produkt-Medien laden …" />}
        {antwort && !antwort.plxProduct && (
          <Alert severity="info">
            Diesem Webinar ist kein PL-X-Produkt zugeordnet — es gibt keine Produkt-Medien zum
            Binden.
          </Alert>
        )}
        {antwort?.plxProduct && !antwort.bereit && (
          <Alert severity="warning">Produkt-Medien sind noch nicht eingerichtet.</Alert>
        )}
        {antwort?.plxProduct && antwort.bereit && (
          <Box display="flex" flexDirection="column" gap={2}>
            {antwort.faecher.map((f) => (
              <Box key={f.slot}>
                <Typography variant="subtitle2" gutterBottom>
                  {PRODUKT_SLOT_LABEL[f.slot]}
                </Typography>
                {!f.cover?.urls ? (
                  <Typography variant="body2" color="text.secondary">
                    Noch kein Bild — erst im Reiter „Produkt-Medien“ hochladen.
                  </Typography>
                ) : (
                  <Box display="flex" gap={1.5} flexWrap="wrap">
                    {variantenFuer(f).map((v) => {
                      const url = coverUrl(f, v);
                      const bindung = `${f.slot}:${v}`;
                      return (
                        <ButtonBase
                          key={v}
                          disabled={!url}
                          onClick={() => url && onWaehle(bindung, url)}
                          data-testid={`kachel-${bindung}`}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: 150,
                            p: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: url ? '#ffffff' : '#f5f5f5',
                            opacity: url ? 1 : 0.5,
                            '&:hover': url ? { borderColor: '#1976d2' } : undefined,
                          }}
                          title={url ? `Binden: ${bindung}` : 'Diese Variante gibt es nicht'}
                        >
                          {url ? (
                            <Box
                              component="img"
                              src={url}
                              alt={`${PRODUKT_SLOT_LABEL[f.slot]} ${PRODUKT_VARIANTE_LABEL[v]}`}
                              sx={{ maxWidth: 130, maxHeight: 120, objectFit: 'contain' }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 130,
                                height: 120,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#9e9e9e',
                                fontSize: 12,
                              }}
                            >
                              nicht vorhanden
                            </Box>
                          )}
                          <Typography variant="caption" sx={{ mt: 0.5 }}>
                            {PRODUKT_VARIANTE_LABEL[v]}
                          </Typography>
                        </ButtonBase>
                      );
                    })}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
      </DialogActions>
    </Dialog>
  );
}
